'use client'
import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/components/button/AccessibilityButton.tsx
import { useEffect as useEffect2 } from "react";
import { useDeviceDetect } from "@wbk/hooks";
import { BottomSheet, Button as Button2, Modal } from "@wbk/ui";
import { FormProvider, useForm, useFormContext as useFormContext6 } from "react-hook-form";
import { useRouter as useRouter3, useTranslation as useTranslation6 } from "@wbk/config";
import { twMerge } from "tailwind-merge";
import { WithGrecaptcha } from "@wbk/api/grecaptcha";
import { AnimatePresence, motion } from "motion/react";
import { AccessibilityIcon, AnimatedCheck } from "@wbk/svg";

// src/components/button/accessibility/InfoForm.tsx
import { useEffect } from "react";
import { Input, Select, ToggleGroup } from "@wbk/ui";
import { Controller, useFormContext } from "react-hook-form";
import { useProfileConfig, useRouter, useTranslation } from "@wbk/config";
import { COUNTRIES, dateAdd, parseDateString } from "@wbk/utils";
import { useAuth, useEmailSpelling } from "@wbk/auth";
import { useGetCities } from "@wbk/api/hooks";
import { jsx, jsxs } from "react/jsx-runtime";
var AccessibilityInfoForm = () => {
  var _a, _b, _c, _d, _e, _f, _r;
  const { locale } = useRouter();
  const { minAge } = useProfileConfig();
  const { user } = useAuth();
  const {
    setValue,
    handleSubmit,
    register,
    control,
    watch,
    trigger,
    formState: { errors }
  } = useFormContext();
  const { t } = useTranslation(["ticketing_common", "ticketing_event"]);
  useEffect(() => {
    if (user) {
      setValue("first_name", user.first_name);
      setValue("last_name", user.last_name);
      setValue("email", user.email);
      setValue("country", user.country || "");
      setValue("nationality", user.nationality || "");
      setValue("gender", user.gender || "");
    }
  }, [user]);
  const mode = watch("mode");
  const email = watch("email");
  const countryOfResidence = watch("country") || (user == null ? void 0 : user.country);
  const { data: cities, error: cityError } = useGetCities(
    { country: countryOfResidence || "", lang: locale },
    { enabled: !!countryOfResidence }
  );
  const emailSuggestion = useEmailSpelling({ email });
  const onSubmit = () => {
    setValue("mode", 1 /* DISABILITY */);
  };
  return /* @__PURE__ */ jsxs("form", { className: "space-y-4", id: `accessibility_step_${mode}`, onSubmit: handleSubmit(onSubmit), children: [
    /* @__PURE__ */ jsxs("fieldset", { className: "flex flex-col gap-2 py-1 sm:flex-row", children: [
      /* @__PURE__ */ jsx("div", { className: "grow", children: /* @__PURE__ */ jsx(
        Input,
        __spreadProps(__spreadValues({
          id: "first_name",
          label: t("ticketing_event:accessibility.first_name"),
          defaultValue: (user == null ? void 0 : user.first_name) || ""
        }, register("first_name", {
          required: t("ticketing_common:validation.required"),
          maxLength: {
            value: 25,
            message: t("ticketing_common:validation.max_length", { max: 25 })
          }
        })), {
          onChange: (value) => {
            setValue("first_name", value);
          },
          error: (_a = errors.first_name) == null ? void 0 : _a.message
        })
      ) }),
      /* @__PURE__ */ jsx("div", { className: "grow", children: /* @__PURE__ */ jsx(
        Input,
        __spreadProps(__spreadValues({
          id: "last_name",
          autoComplete: "off",
          label: t("ticketing_event:accessibility.last_name"),
          defaultValue: (user == null ? void 0 : user.last_name) || ""
        }, register("last_name", {
          required: t("ticketing_common:validation.required"),
          maxLength: {
            value: 25,
            message: t("ticketing_common:validation.max_length", { max: 25 })
          }
        })), {
          onChange: (value) => {
            setValue("last_name", value);
          },
          error: (_b = errors.last_name) == null ? void 0 : _b.message
        })
      ) })
    ] }),
    /* @__PURE__ */ jsx(
      Input,
      __spreadProps(__spreadValues({
        type: "date",
        className: "w-full",
        pattern: "\\d{4}-\\d{2}-\\d{2}",
        max: (_e = (_d = (_c = dateAdd({
          date: /* @__PURE__ */ new Date(),
          value: -minAge,
          interval: "year"
        })) == null ? void 0 : _c.toISOString()) == null ? void 0 : _d.split("T")) == null ? void 0 : _e[0],
        label: t("ticketing_event:accessibility.birthdate"),
        defaultValue: parseDateString(user == null ? void 0 : user.dob) || ""
      }, register("dob", { required: t("ticketing_common:validation.required") })), {
        onChange: (value) => {
          setValue("dob", value);
        },
        error: (_f = errors.dob) == null ? void 0 : _f.message
      })
    ),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: "nationality",
        control,
        rules: { required: t("ticketing_common:validation.required") },
        defaultValue: (user == null ? void 0 : user.nationality) || "",
        render: (_g) => {
          var { field: _h } = _g, _i = _h, { onChange } = _i, props = __objRest(_i, ["onChange"]);
          var _a2;
          return /* @__PURE__ */ jsx(
            Select,
            __spreadProps(__spreadValues({
              id: "nationality"
            }, props), {
              portal: false,
              autoComplete: "off",
              onChange: (selected) => onChange((selected == null ? void 0 : selected.value) || ""),
              label: t("ticketing_event:accessibility.nationality"),
              options: COUNTRIES.map((c) => ({
                id: c.code,
                value: c.code,
                text: c[`name_${locale}`] || c.name,
                icon: /* @__PURE__ */ jsx(
                  "img",
                  {
                    className: "shrink-0",
                    src: `https://wbk-assets.s3.me-south-1.amazonaws.com/flags/w80/${c.code.toLowerCase()}.png`,
                    width: 20,
                    height: 14,
                    alt: c.name
                  }
                )
              })),
              error: (_a2 = errors.nationality) == null ? void 0 : _a2.message
            })
          );
        }
      }
    ),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: "gender",
        control,
        rules: { required: t("ticketing_common:validation.required") },
        defaultValue: (user == null ? void 0 : user.gender) || "",
        render: ({ field }) => {
          var _a2;
          return /* @__PURE__ */ jsx(
            ToggleGroup,
            __spreadProps(__spreadValues({
              label: t("ticketing_event:accessibility.gender"),
              options: [
                { text: t("ticketing_event:accessibility.male"), value: "male" },
                { text: t("ticketing_event:accessibility.female"), value: "female" },
                {
                  text: t("ticketing_event:accessibility.prefer_not_to_say"),
                  value: "prefer_not_to_say"
                }
              ]
            }, field), {
              type: "single",
              error: (_a2 = errors.gender) == null ? void 0 : _a2.message
            })
          );
        }
      }
    ),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: "country",
        control,
        rules: { required: t("ticketing_common:validation.required") },
        defaultValue: (user == null ? void 0 : user.country) || "",
        render: (_j) => {
          var { field: _k } = _j, _l = _k, { onChange } = _l, props = __objRest(_l, ["onChange"]);
          var _a2;
          return /* @__PURE__ */ jsx(
            Select,
            __spreadProps(__spreadValues({
              id: "country"
            }, props), {
              portal: false,
              autoComplete: "off",
              onChange: (selected) => {
                onChange((selected == null ? void 0 : selected.value) || "");
                setValue("city", "", { shouldValidate: true });
              },
              label: t("ticketing_event:accessibility.country_of_residence"),
              options: COUNTRIES.map((c) => ({
                id: c.code,
                value: c.code,
                text: c[`name_${locale}`] || c.name,
                icon: /* @__PURE__ */ jsx(
                  "img",
                  {
                    className: "shrink-0",
                    src: `https://wbk-assets.s3.me-south-1.amazonaws.com/flags/w80/${c.code.toLowerCase()}.png`,
                    width: 20,
                    height: 14,
                    alt: c.name
                  }
                )
              })),
              error: (_a2 = errors.country) == null ? void 0 : _a2.message
            })
          );
        }
      }
    ),
    /* @__PURE__ */ jsx(
      Controller,
      {
        name: "city",
        control,
        rules: { required: t("auth_common:validation.required") },
        defaultValue: "",
        render: (_m) => {
          var { field: _n } = _m, _o = _n, { onChange } = _o, props = __objRest(_o, ["onChange"]);
          var _a2, _b2;
          return /* @__PURE__ */ jsx(
            Select,
            __spreadProps(__spreadValues(__spreadProps(__spreadValues({}, props), {
              id: "city",
              autoComplete: "off"
            }), props), {
              portal: false,
              onChange: (selected) => {
                onChange((selected == null ? void 0 : selected.value) || "");
                trigger("city");
              },
              label: t("ticketing_event:accessibility.city"),
              options: (_a2 = cities || []) == null ? void 0 : _a2.map((city, i) => ({
                id: i.toString(),
                value: city.name,
                text: city.name
              })),
              error: ((_b2 = errors.city) == null ? void 0 : _b2.message) || (cityError == null ? void 0 : cityError.message)
            })
          );
        }
      },
      countryOfResidence
    ),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(
        Controller,
        {
          control,
          name: "email",
          rules: { required: t("ticketing_common:validation.required") },
          defaultValue: (user == null ? void 0 : user.email) || "",
          render: (_p) => {
            var { field: _q } = _p, props = __objRest(_q, []);
            var _a2;
            return /* @__PURE__ */ jsx(
              Input,
              __spreadProps(__spreadValues({
                id: "email",
                autoComplete: "off",
                label: t("ticketing_event:accessibility.email_to_receive_tickets")
              }, props), {
                error: (_a2 = errors.email) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx("p", { className: "text-text-secondary pt-0.5 text-sm", children: t("ticketing_event:accessibility.email_notice") }),
      emailSuggestion && /* @__PURE__ */ jsxs(
        "div",
        {
          className: `text-text-tertiary absolute w-full rounded-lg bg-yellow-100 p-2 text-sm ${((_r = errors.email) == null ? void 0 : _r.message) ? "bottom-0" : "-bottom-4"}`,
          children: [
            t("common:auth.did_you_mean"),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => setValue("email", emailSuggestion.full, {
                  shouldValidate: true
                }),
                className: "text-primary mx-1 cursor-pointer font-semibold hover:underline",
                children: emailSuggestion.full
              }
            )
          ]
        }
      )
    ] })
  ] });
};
var InfoForm_default = AccessibilityInfoForm;

// src/components/button/accessibility/TicketForm.tsx
import { Controller as Controller2, useFormContext as useFormContext2 } from "react-hook-form";
import { RadioGroup, TextArea } from "@wbk/ui";
import { useRouter as useRouter2, useTranslation as useTranslation2 } from "@wbk/config";
import { useRequestDisabilityTickets } from "@wbk/api/hooks";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var TYPES = [
  "wheelchair_user",
  "easy_access_standard",
  // 'companion_tickets',
  "easy_access_amenity"
];
var AccessibilityTicketForm = () => {
  var _a;
  const { t } = useTranslation2(["ticketing_common", "ticketing_event"]);
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors }
  } = useFormContext2();
  const { locale } = useRouter2();
  const { mutateAsync: submit, error } = useRequestDisabilityTickets();
  const mode = watch("mode");
  const onSubmit = (data) => __async(void 0, null, function* () {
    try {
      setValue("loading", true);
      const body = new FormData();
      const exclude = ["mode", "direction", "success", "loading"];
      for (const key in data) {
        if (!exclude.includes(key)) {
          const value = data[key];
          body.append(key, value);
        }
      }
      body.append("lang", locale);
      yield submit({ body });
      setValue("success", true);
    } finally {
      setValue("loading", false);
    }
  });
  return /* @__PURE__ */ jsxs2("form", { className: "space-y-4", id: `accessibility_step_${mode}`, onSubmit: handleSubmit(onSubmit), children: [
    (error == null ? void 0 : error.message) && /* @__PURE__ */ jsx2("p", { className: "text-error py-2", children: error.message }),
    /* @__PURE__ */ jsx2(
      Controller2,
      {
        name: "ticket_type",
        control,
        defaultValue: "",
        rules: { required: t("ticketing_common:validation.required") },
        render: ({ field, fieldState: { error: error2 } }) => /* @__PURE__ */ jsx2(
          RadioGroup,
          __spreadProps(__spreadValues({}, field), {
            label: /* @__PURE__ */ jsxs2("span", { className: error2 ? "text-error" : "", children: [
              t("ticketing_event:accessibility.ticket_types"),
              !!error2 && /* @__PURE__ */ jsx2("span", { className: "text-error text-sm", children: ` - ${error2 == null ? void 0 : error2.message}` })
            ] }),
            className: "items-start [&>button]:mt-1",
            options: TYPES.map((type) => {
              return {
                text: /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
                  /* @__PURE__ */ jsx2("p", { children: t(`ticketing_event:accessibility.${type}`) }),
                  /* @__PURE__ */ jsx2("p", { className: "text-text-tertiary text-sm", children: t(`ticketing_event:accessibility.${type}_desc`) })
                ] }),
                value: type
              };
            }),
            error: error2 == null ? void 0 : error2.message
          })
        )
      }
    ),
    /* @__PURE__ */ jsx2(
      TextArea,
      __spreadProps(__spreadValues({
        id: "more_info",
        autoComplete: "off",
        className: "min-h-32",
        label: t("ticketing_event:accessibility.more_info"),
        placeholder: t("ticketing_event:accessibility.more_info_placeholder")
      }, register("more_info", {
        required: t("ticketing_common:validation.required")
      })), {
        onChange: (value) => {
          setValue("more_info", value);
        },
        error: (_a = errors.more_info) == null ? void 0 : _a.message
      })
    )
  ] });
};
var TicketForm_default = AccessibilityTicketForm;

// src/components/button/accessibility/DisabilityForm.tsx
import { useRef } from "react";
import { Controller as Controller3, useFormContext as useFormContext3 } from "react-hook-form";
import { Dropzone, Select as Select2 } from "@wbk/ui";
import { useTranslation as useTranslation3 } from "@wbk/config";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var AccessibilityDisabilityForm = () => {
  const { t } = useTranslation3(["ticketing_common", "ticketing_event"]);
  const { setValue, handleSubmit, watch, control } = useFormContext3();
  const mode = watch("mode");
  const formRef = useRef(null);
  const onSubmit = () => {
    setValue("mode", 2 /* TICKET */);
  };
  return /* @__PURE__ */ jsxs3(
    "form",
    {
      className: "group space-y-4",
      id: `accessibility_step_${mode}`,
      onSubmit: handleSubmit(onSubmit),
      ref: formRef,
      children: [
        /* @__PURE__ */ jsx3(
          "div",
          {
            className: "absolute top-0 z-0 h-full w-full",
            onDragOver: (e) => {
              e.preventDefault();
            },
            onDrop: (e) => {
              var _a;
              e.preventDefault();
              if (e.dataTransfer.files.length) {
                setValue("disability_card", e.dataTransfer.files[0]);
              }
              e.currentTarget.classList.remove("z-50");
              e.currentTarget.classList.add("z-0");
              (_a = formRef.current) == null ? void 0 : _a.removeAttribute("data-dragging");
            },
            onDragEnter: (e) => {
              var _a;
              e.currentTarget.classList.add("z-50");
              e.currentTarget.classList.remove("z-0");
              (_a = formRef.current) == null ? void 0 : _a.setAttribute("data-dragging", "true");
            },
            onDragLeave: (e) => {
              var _a;
              e.currentTarget.classList.remove("z-50");
              e.currentTarget.classList.add("z-0");
              (_a = formRef.current) == null ? void 0 : _a.removeAttribute("data-dragging");
            }
          }
        ),
        /* @__PURE__ */ jsx3(
          Controller3,
          {
            name: "disability_type",
            control,
            rules: { required: t("ticketing_common:validation.required") },
            defaultValue: "",
            render: (_a) => {
              var _b = _a, { field: _c } = _b, _d = _c, { onChange } = _d, props = __objRest(_d, ["onChange"]), { fieldState: { error } } = _b;
              return /* @__PURE__ */ jsx3(
                Select2,
                __spreadProps(__spreadValues({
                  id: "disability_type"
                }, props), {
                  portal: false,
                  autoComplete: "off",
                  onChange: (selected) => onChange((selected == null ? void 0 : selected.value) || ""),
                  label: t("ticketing_event:accessibility.disability_type"),
                  className: "z-10",
                  options: ["wheelchair_access", "free_space_to_stretch_their_legs"].map((c) => ({
                    id: c,
                    value: c,
                    text: t(`ticketing_event:accessibility.${c}`)
                  })),
                  error: error == null ? void 0 : error.message
                })
              );
            }
          }
        ),
        /* @__PURE__ */ jsx3(
          Controller3,
          {
            rules: { required: t("ticketing_common:validation.required") },
            control,
            name: "disability_card",
            render: (_e) => {
              var _f = _e, { field: _g } = _f, _h = _g, { onChange } = _h, props = __objRest(_h, ["onChange"]), { fieldState: { error } } = _f;
              return /* @__PURE__ */ jsxs3("div", { children: [
                /* @__PURE__ */ jsx3("p", { className: "font-semibold", children: t("ticketing_event:accessibility.upload_disability_card") }),
                /* @__PURE__ */ jsx3("p", { className: "text-text-secondary pb-2 text-sm", children: t("ticketing_event:accessibility.upload_disability_card_desc") }),
                /* @__PURE__ */ jsx3(
                  Dropzone,
                  __spreadProps(__spreadValues({}, props), {
                    onChange,
                    helperText: t("ticketing_event:accessibility.upload_limit"),
                    error: error == null ? void 0 : error.message,
                    inputProps: { accept: "image/*,application/pdf" }
                  })
                )
              ] });
            }
          }
        )
      ]
    }
  );
};
var DisabilityForm_default = AccessibilityDisabilityForm;

// src/components/button/accessibility/Steps.tsx
import { useFormContext as useFormContext4 } from "react-hook-form";
import { useTranslation as useTranslation4 } from "@wbk/config";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var AccessibilitySteps = () => {
  const { t } = useTranslation4("ticketing_event");
  const { watch } = useFormContext4();
  const mode = watch("mode") || 0;
  return /* @__PURE__ */ jsxs4("div", { className: "grid grid-cols-3 gap-2", children: [
    /* @__PURE__ */ jsxs4("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx4("div", { className: "bg-paper-level-1 relative h-0.5 overflow-hidden rounded-md", children: /* @__PURE__ */ jsx4(
        "div",
        {
          "data-mode": mode,
          className: "bg-body-dark absolute inset-0 w-full rounded-md transition-all data-[mode=0]:w-[80%]"
        }
      ) }),
      /* @__PURE__ */ jsx4("p", { className: "text-text-secondary text-xs", children: t("ticketing_event:accessibility.step_info") })
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx4("div", { className: "bg-paper-level-1 relative h-0.5 overflow-hidden rounded-md", children: /* @__PURE__ */ jsx4(
        "div",
        {
          "data-mode": mode,
          className: "bg-body-dark absolute inset-0 w-full rounded-md transition-all data-[mode=0]:w-0 data-[mode=1]:w-[80%] data-[mode=1]:duration-1000"
        }
      ) }),
      /* @__PURE__ */ jsx4("p", { className: "text-text-secondary text-xs", children: t("ticketing_event:accessibility.step_disability") })
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx4("div", { className: "bg-paper-level-1 relative h-0.5 overflow-hidden rounded-md", children: /* @__PURE__ */ jsx4(
        "div",
        {
          "data-mode": mode,
          className: "bg-body-dark absolute inset-0 w-full rounded-md transition-all data-[mode=0]:w-0 data-[mode=1]:w-0 data-[mode=2]:w-[80%] data-[mode=2]:duration-1000"
        }
      ) }),
      /* @__PURE__ */ jsx4("p", { className: "text-text-secondary text-xs", children: t("ticketing_event:accessibility.step_ticket") })
    ] })
  ] });
};
var Steps_default = AccessibilitySteps;

// src/components/button/accessibility/Actions.tsx
import { useFormContext as useFormContext5 } from "react-hook-form";
import { Button } from "@wbk/ui";
import { useTranslation as useTranslation5 } from "@wbk/config";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var AccessibilityFormActions = ({ onClose }) => {
  const { t } = useTranslation5("ticketing_common");
  const { setValue, watch } = useFormContext5();
  const mode = watch("mode");
  const loading = watch("loading");
  const success = watch("success");
  const handleBack = () => {
    setValue("direction", -1);
    if (mode === 2 /* TICKET */) {
      setValue("mode", 1 /* DISABILITY */);
    } else {
      setValue("mode", 0 /* INFO */);
    }
  };
  const handleNext = () => {
    setValue("direction", 1);
  };
  if (success) {
    return /* @__PURE__ */ jsx5("div", { className: "border-paper bg-body rounded-b-md border-t p-4 text-center", children: /* @__PURE__ */ jsx5(Button, { theme: "white", className: "px-10 font-semibold", onClick: onClose, children: t("ticketing_common:back") }) });
  }
  return /* @__PURE__ */ jsxs5("div", { className: "border-paper bg-body flex items-center justify-between gap-4 rounded-b-md border-t p-4", children: [
    /* @__PURE__ */ jsx5("div", { children: mode !== 0 /* INFO */ && /* @__PURE__ */ jsx5(Button, { theme: "white", shape: "outlined", onClick: handleBack, disabled: loading, children: t("ticketing_common:back") }) }),
    /* @__PURE__ */ jsxs5("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx5(Button, { theme: "white", shape: "text", onClick: onClose, disabled: loading, children: t("ticketing_common:cancel") }),
      /* @__PURE__ */ jsx5(
        Button,
        {
          type: "submit",
          form: `accessibility_step_${mode}`,
          className: "px-6",
          onClick: handleNext,
          loading,
          children: t("ticketing_common:next")
        }
      )
    ] })
  ] });
};
var Actions_default = AccessibilityFormActions;

// src/components/button/AccessibilityButton.tsx
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var EventAccessibilityButton = ({ slug, type = "event", className }) => {
  const { isMobile } = useDeviceDetect();
  const methods = useForm({
    defaultValues: {
      slug,
      type,
      mode: 0 /* INFO */
    }
  });
  return /* @__PURE__ */ jsx6(WithGrecaptcha, { children: /* @__PURE__ */ jsx6(FormProvider, __spreadProps(__spreadValues({}, methods), { children: isMobile ? /* @__PURE__ */ jsx6(
    BottomSheet,
    {
      fullscreen: true,
      sectionClassName: "px-4 pb-32",
      render: ({ open }) => /* @__PURE__ */ jsx6(ActionButton, { onClick: open, className }),
      body: () => /* @__PURE__ */ jsx6(Form, {}),
      stickyFooter: ({ close }) => /* @__PURE__ */ jsx6(Actions_default, { onClose: close })
    }
  ) : /* @__PURE__ */ jsx6(
    Modal,
    {
      closeButton: true,
      className: "overflow-y-auto p-4 pb-28",
      contentClassName: "max-w-xl",
      render: ({ open }) => /* @__PURE__ */ jsx6(ActionButton, { onClick: open, className }),
      body: () => /* @__PURE__ */ jsx6(Form, {}),
      stickyFooter: ({ close }) => /* @__PURE__ */ jsx6(Actions_default, { onClose: close })
    }
  ) })) });
};
var Form = () => {
  const { t } = useTranslation6("ticketing_event");
  const { locale } = useRouter3();
  const { watch, reset } = useFormContext6();
  const mode = watch("mode");
  const direction = watch("direction");
  const isSuccess = watch("success");
  useEffect2(() => {
    return () => {
      reset();
    };
  }, []);
  if (isSuccess) {
    return /* @__PURE__ */ jsxs6("div", { className: "space-y-4 overflow-hidden pb-20", children: [
      /* @__PURE__ */ jsx6(AnimatedCheck, { className: "text-success mx-auto h-40 w-40" }),
      /* @__PURE__ */ jsxs6("div", { className: "space-y-2 text-center", children: [
        /* @__PURE__ */ jsx6("h2", { className: "pb-1 text-2xl font-semibold", children: t("ticketing_event:accessibility.thank_you_accessibility") }),
        /* @__PURE__ */ jsx6("p", { className: "text-text-secondary text-sm", children: t("ticketing_event:accessibility.thank_you_accessibility_text_1") })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs6("div", { className: "relative space-y-4 overflow-hidden", children: [
    /* @__PURE__ */ jsxs6("div", { children: [
      /* @__PURE__ */ jsx6(AccessibilityIcon, { className: "h-10 w-10 shrink-0" }),
      /* @__PURE__ */ jsx6("h4", { children: t("ticketing_event:accessibility.ticket_request") }),
      /* @__PURE__ */ jsx6("p", { className: "text-text-secondary text-sm", children: t("ticketing_event:accessibility.ticket_request_desc") })
    ] }),
    /* @__PURE__ */ jsx6(Steps_default, {}),
    /* @__PURE__ */ jsx6(AnimatePresence, { initial: false, custom: direction, mode: "wait", children: /* @__PURE__ */ jsx6(
      motion.div,
      {
        custom: direction,
        variants: {
          enter: (dir) => {
            const dirVal = locale === "ar" ? -100 : 100;
            return {
              x: dir < 0 ? `${-dirVal}%` : `${dirVal}%`,
              opacity: 0
            };
          },
          center: { x: 0, opacity: 1 },
          exit: (dir) => {
            const dirVal = locale === "ar" ? -100 : 100;
            return {
              x: dir > 0 ? `${-dirVal}%` : `${dirVal}%`,
              opacity: 0
            };
          }
        },
        initial: "enter",
        animate: "center",
        exit: "exit",
        transition: { duration: 0.2 },
        children: mode === 2 /* TICKET */ ? /* @__PURE__ */ jsx6(TicketForm_default, {}) : mode === 1 /* DISABILITY */ ? /* @__PURE__ */ jsx6(DisabilityForm_default, {}) : /* @__PURE__ */ jsx6(InfoForm_default, {})
      },
      mode
    ) })
  ] });
};
var ActionButton = ({ onClick, className }) => {
  const { t } = useTranslation6(["ticketing_event"]);
  return /* @__PURE__ */ jsxs6(
    Button2,
    {
      onClick,
      theme: "white",
      shape: "outlined",
      className: twMerge(
        "mx-auto min-h-[50px] w-full justify-center gap-1 text-lg font-semibold leading-none lg:mx-0",
        className
      ),
      children: [
        /* @__PURE__ */ jsx6(AccessibilityIcon, { className: "h-6 w-6 shrink-0" }),
        t("ticketing_event:accessibility.get_tickets")
      ]
    }
  );
};
var AccessibilityButton_default = EventAccessibilityButton;

// src/components/button/Back.tsx
import { twMerge as twMerge2 } from "tailwind-merge";
import { Button as Button3, Typography } from "@wbk/ui";
import { ChevronIcon } from "@wbk/svg";
import { useRouter as useRouter4 } from "@wbk/config";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
var BackButton = (_a) => {
  var _b = _a, { text, className = "", onClick } = _b, props = __objRest(_b, ["text", "className", "onClick"]);
  const { replace } = useRouter4();
  const handleGoBack = () => {
    const pathname = window.location.pathname;
    const previousPath = pathname.split("/").slice(0, -1).join("/") || "/";
    replace(previousPath);
  };
  return /* @__PURE__ */ jsxs7(
    Button3,
    __spreadProps(__spreadValues({
      theme: "transparent",
      shape: "text",
      "data-testid": "ticketing_generic_go_back_button",
      className: twMerge2(
        "hover:bg-primary/10 shrink-0 items-center gap-1 px-0 py-0.5 text-xs !ring-0 focus:underline",
        className
      ),
      onClick: onClick || handleGoBack
    }, props), {
      children: [
        /* @__PURE__ */ jsx7(ChevronIcon, { className: "h-2.5 w-2.5 ltr:rotate-180" }),
        /* @__PURE__ */ jsx7(Typography, { variant: "body-XS", as: "span", className: "ltr:pr-1 rtl:pl-1", children: text })
      ]
    })
  );
};
var Back_default = BackButton;

export {
  Back_default,
  AccessibilityButton_default
};
//# sourceMappingURL=chunk-YL5YCJF5.mjs.map